import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLazyGetFieldPhotoBlobQuery } from '@shared/api';
import type { PhotoFileProblem } from '@entities/field';

const Thumb = styled.button`
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  overflow: hidden;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Placeholder = styled.div<{ $bad?: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  text-align: center;
  font-size: 12px;
  color: ${({ theme, $bad }) => ($bad ? theme.colors.status.danger : theme.colors.text.muted)};
`;

/** Подпись заглушки вместо превью. */
function placeholderText(problem: PhotoFileProblem | null, failed: boolean): string {
  if (problem === 'not_image') return 'файл не изображение';
  if (problem === 'broken') return 'файл повреждён — не открывается';
  return failed ? 'не загрузилось' : 'загрузка…';
}

/**
 * Превью фото площадки: байты берутся лениво через RBAC-запрос
 * `GET /photos/{id}/data` (Authorization из baseQuery), рендер — objectURL.
 * Клик — открыть полноразмер в новой вкладке. Серверных миниатюр нет —
 * оригинал уже сжат на устройстве (МП-3: длинная сторона 2048 px).
 * Файл, который не изображение (сервер по сигнатуре отдаёт его не как картинку)
 * или не раскрывается браузером, показывается заглушкой, а не пустым превью, и
 * сообщается галерее (`onProblem`) — для метки у кадра.
 */
export function FieldPhotoThumb({
  photoId,
  alt,
  onProblem,
}: {
  photoId: string;
  alt: string;
  onProblem?: (problem: PhotoFileProblem) => void;
}) {
  const [trigger] = useLazyGetFieldPhotoBlobQuery();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [problem, setProblem] = useState<PhotoFileProblem | null>(null);
  // Колбэк родителя — через ref: новая функция на каждом рендере не должна
  // перезапускать загрузку байтов.
  const onProblemRef = useRef(onProblem);
  useEffect(() => {
    onProblemRef.current = onProblem;
  });

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    trigger(photoId, true)
      .unwrap()
      .then((blob) => {
        if (cancelled) return;
        // Сервер сверяет сигнатуру файла: не картинка → application/octet-stream.
        if (blob.type && !blob.type.startsWith('image/')) {
          setProblem('not_image');
          onProblemRef.current?.('not_image');
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photoId, trigger]);

  return (
    <Thumb
      type="button"
      onClick={() => {
        if (url && !problem) window.open(url, '_blank', 'noopener');
      }}
      title={alt}
      aria-label={alt}
    >
      {url && !problem ? (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          onError={() => {
            setProblem('broken');
            onProblemRef.current?.('broken');
          }}
        />
      ) : (
        <Placeholder $bad={!!problem}>{placeholderText(problem, failed)}</Placeholder>
      )}
    </Thumb>
  );
}
